import { context, propagation } from '@opentelemetry/api'
import { QueryParameterBag } from '@smithy/types'

import { AwsCredentialIdentity } from '@aws-sdk/types'

import {
  AwsHttpHeaders,
  DroidMapError,
  DroidMapHttpHeaders,
  HttpResponseError,
  W3CTraceContextHttpHeaders,
  ensureError,
  retryFetch,
  toParameterString,
} from '@droidmap/shared-utils'

import { DroidMapMetadata } from './service-events.js'

export type ServiceClientContext = {
  getToken: () => Promise<string>
  endpoint: string
  isInternal?: boolean
  extraHeaders?: Record<string, string>
  serviceCredentials?: AwsCredentialIdentity
  droidMapMetadata?: DroidMapMetadata
}

export class ServiceClient {
  public readonly serviceName: string
  public getToken: () => Promise<string>
  public readonly endpoint: string
  public readonly isInternal: boolean
  protected readonly extraHeaders: Record<string, string>
  public readonly serviceCredentials?: AwsCredentialIdentity
  private droidMapMetadata?: DroidMapMetadata
  private readonly isoDatePattern =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/

  constructor(input: ServiceClientContext & { serviceName: string }) {
    this.serviceName = input.serviceName
    this.getToken = input.getToken
    this.endpoint = input.endpoint
    this.isInternal = input.isInternal || false
    this.extraHeaders = input.extraHeaders || {}
    this.serviceCredentials = input.serviceCredentials
    this.droidMapMetadata = input.droidMapMetadata
  }

  protected dateReviver = (key: string, value: unknown) => {
    if (typeof value === 'string' && this.isoDatePattern.test(value)) {
      const date = new Date(value)
      if (!isNaN(date.getTime())) {
        return date
      }
    }
    return value
  }

  protected callApiRouteWithToken = async <T, U>(input: {
    method: string
    path: string
    queryParameters?: QueryParameterBag
    body?: T
    metadata?: DroidMapMetadata
  }): Promise<U> => {
    // If the metadata in the input is not set, use the metadata from the client
    // This allows us to set the metadata once on the client and not have to pass
    // it in every call which is useful for the frontend where the metadata changes
    // infrequently. While still allowing the metadata to be set on a per call basis
    // which is need for internal service calls.
    const metadata = input.metadata || this.droidMapMetadata
    if (!metadata) {
      throw new DroidMapError('No DroidMapMetadata provided for service call')
    }

    const token = await this.getToken()

    const response = await retryFetch(() =>
      this.buildRequest(input, metadata, token),
    )

    if (response.ok) {
      try {
        const jsonText = await response.text()
        const data = JSON.parse(jsonText, this.dateReviver) as U
        return data
      } catch (err) {
        const error = ensureError(err)
        // `HttpResponseError`, not a bare `DroidMapError`: this is a *downstream
        // call* that failed, and the shared Hono error handler tells the two
        // apart by exactly this class. As a `DroidMapError` it landed in the
        // handler's 400 arm, so a service that called another service and got
        // back an unparseable body answered its own caller "400" with this
        // transport string as the message — which a UI surfacing 400s then
        // showed to a customer (droidmap#2536).
        throw new HttpResponseError(
          `Failed to parse JSON response from ${input.method} ${input.path} (${response.status} ${response.statusText}): ${error.message}`,
          { status: response.status, cause: error },
        )
      }
    }

    // You need to consume the response body for failed requests in order to reuse the connection
    await response.text()
    throw new HttpResponseError(
      `Failed to call api route ${input.method} ${input.path} - ${response.status} ${response.statusText}`,
      { status: response.status },
    )
  }

  /**
   * Builds up a request object for the fetch api
   *
   * @param input
   * @param token
   * @returns
   */
  private buildRequest = <T>(
    input: {
      method: string
      path: string
      queryParameters?: QueryParameterBag
      body?: T
    },
    metadata: DroidMapMetadata,
    token: string,
  ): Request => {
    const { method, path, queryParameters, body } = input

    let url = `${this.endpoint}${path}`
    if (queryParameters) {
      // add query params to url
      url = `${url}?${toParameterString(queryParameters)}`
    }

    // Auth headers
    const headers = this.buildRequestHeaders(token, metadata)

    const options = {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      body: method === 'GET' ? undefined : JSON.stringify(body),
    }

    return new Request(url, options)
  }

  buildRequestHeaders(token: string, metadata: DroidMapMetadata) {
    const authHeaders = {
      Authorization: `Bearer ${token}`,
    }

    const droidmapHeaders = this.buildDroidMapHeaders(metadata)

    const headers: Record<string, string> = {
      ...this.extraHeaders,
      ...authHeaders,
      ...droidmapHeaders,
    }

    // Inject W3C trace context (traceparent, tracestate) from the active OTel span.
    propagation.inject(context.active(), headers)

    // Fallback: if inject was a no-op (no SDK registered / no active span) but
    // metadata carries trace headers (e.g. from Lambda middleware), use those
    // so propagation still works without an active OTel context.
    if (
      !headers[W3CTraceContextHttpHeaders.TRACE_PARENT] &&
      metadata.traceParent
    ) {
      headers[W3CTraceContextHttpHeaders.TRACE_PARENT] = metadata.traceParent
    }
    if (
      !headers[W3CTraceContextHttpHeaders.TRACE_STATE] &&
      metadata.traceState
    ) {
      headers[W3CTraceContextHttpHeaders.TRACE_STATE] = metadata.traceState
    }
    if (!headers[AwsHttpHeaders.X_AMZN_TRACE_ID] && metadata.xrayTraceId) {
      headers[AwsHttpHeaders.X_AMZN_TRACE_ID] = metadata.xrayTraceId
    }

    return headers
  }

  buildDroidMapHeaders = (
    metadata: DroidMapMetadata,
  ): Record<string, string> => {
    const headers: Record<string, string> = {}

    if (metadata) {
      if (this.isInternal) {
        headers[DroidMapHttpHeaders.X_DROIDMAP_INTERNAL] = 'true'
      }
      // Always set calling-user-id header for both internal and external calls
      headers[DroidMapHttpHeaders.X_DROIDMAP_CALLING_USER_ID] = metadata.userId
      headers[DroidMapHttpHeaders.X_DROIDMAP_ORG] = metadata.orgId
      if (metadata.projectId) {
        headers[DroidMapHttpHeaders.X_DROIDMAP_PROJECT] = metadata.projectId
      }
      if (metadata.actingStaffId) {
        headers[DroidMapHttpHeaders.X_DROIDMAP_ACTING_STAFF_ID] =
          metadata.actingStaffId
        if (metadata.actingStaffKind) {
          headers[DroidMapHttpHeaders.X_DROIDMAP_ACTING_STAFF_KIND] =
            metadata.actingStaffKind
        }
      }
    } else {
      throw new DroidMapError('No DroidMapMetadata provided for service call')
    }

    return headers
  }

  public setActiveOrg = (orgId: string) => {
    if (this.droidMapMetadata) {
      this.droidMapMetadata = {
        ...this.droidMapMetadata,
        orgId,
      }
    } else {
      throw new DroidMapError('No DroidMapMetadata currently set for client')
    }
  }

  public setActiveProject = (projectId: string) => {
    if (this.droidMapMetadata) {
      this.droidMapMetadata = {
        ...this.droidMapMetadata,
        projectId,
      }
    } else {
      throw new DroidMapError('No DroidMapMetadata currently set for client')
    }
  }

  public getDroidMapMetadata = () => {
    return this.droidMapMetadata
  }
}
